import React, { useEffect, useState } from 'react';
import { transactionsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

interface AccountsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  eventUuid: string;
  transaction?: any; // If editing, pass transaction object
}

const initialForm = {
  type: 'income',
  description: '',
  from_to: '',
  amount: '',
  transaction_date: '',
};

const AccountsModal: React.FC<AccountsModalProps> = ({ isOpen, onClose, onSave, eventUuid, transaction }) => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    if (isOpen && transaction) {
      // Format transaction_date for input type="date"
      let formattedDate = '';
      if (transaction.transaction_date) {
        const date = new Date(transaction.transaction_date);
        formattedDate = date.toISOString().split('T')[0];
      }
      setForm({
        type: transaction.type || 'income',
        description: transaction.description || '',
        from_to: transaction.from_to || '',
        amount: transaction.amount ? String(transaction.amount) : '',
        transaction_date: formattedDate,
      });
    } else if (isOpen) {
      setForm(initialForm);
    }
    setError('');
  }, [isOpen, transaction]);

  // Auto-dismiss error after 10 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    setLoading(true);
    setError('');
    const payload = {
      type: form.type,
      description: form.description,
      from_to: form.from_to,
      amount: parseFloat(form.amount),
      transaction_date: form.transaction_date || null,
      eventUuid,
      user_uuid: user?.uuid,
    };
    if (transaction && transaction.uuid) {
      // Edit existing transaction
      transactionsAPI.update(transaction.uuid, payload)
        .then(() => onSave())
        .catch(() => setError('Failed to update transaction'))
        .finally(() => setLoading(false));
    } else {
      // Add new transaction
      transactionsAPI.create(payload)
        .then(() => onSave())
        .catch(() => setError('Failed to add transaction'))
        .finally(() => setLoading(false));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-xl shadow-xl p-8 w-full max-w-md relative">
        <button className="absolute top-4 right-4 text-gray-400 hover:text-gray-700" onClick={onClose}>&times;</button>
        <h2 className="text-2xl font-bold mb-4">{transaction ? 'Edit Transaction' : 'Add Transaction'}</h2>
        {loading ? (
          <div className="text-center py-8 text-primary-600">Saving...</div>
        ) : (
          <form className="space-y-4" onSubmit={e => { e.preventDefault(); handleSave(); }}>
            <div>
              <label className="block text-sm font-semibold mb-1">Type</label>
              <select name="type" value={form.type} onChange={handleChange} className="w-full px-4 py-2 border rounded-lg">
                <option value="income">Income</option>
                <option value="expense">Expense</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">From/To</label>
              <input type="text" name="from_to" value={form.from_to} onChange={handleChange} className="w-full px-4 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Description</label>
              <input type="text" name="description" value={form.description} onChange={handleChange} className="w-full px-4 py-2 border rounded-lg" required />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Transaction Date</label>
              <input type="date" name="transaction_date" value={form.transaction_date} onChange={handleChange} className="w-full px-4 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Amount</label>
              <input type="number" name="amount" value={form.amount} onChange={handleChange} className="w-full px-4 py-2 border rounded-lg" required min="0" step="0.01" />
            </div>
            {transaction && transaction.uuid && (
              <input type="hidden" name="uuid" value={transaction.uuid} />
            )}
            {error && <div className="text-red-500 text-sm">{error}</div>}
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" className="px-4 py-2 rounded-lg bg-gray-200" onClick={onClose}>Cancel</button>
              <button type="submit" className="px-4 py-2 rounded-lg bg-primary-600 text-white font-semibold">Save</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AccountsModal;
